/**
 * Episode Judge Evaluation View
 *
 * Dedicated page for evaluating episode conversations with the Judge agent.
 * Includes multi-persona improvement loop (Story 24.4).
 *
 * Story: 24.2 - Evolve Single Episode Eval
 * Story: 24.4 - Multi-Persona Content Improvement Engine
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useFetchEpisodeForTesting,
  useEvaluateConversation,
  useRunSimulation,
  useContentJudge,
  useSummarizeJudges,
  useImproveEpisode,
  type ConversationMessage,
  type ToolCallRecord,
  type EvaluateConversationResponse,
  type CriteriaScore,
} from '../services/testingApi';
import { useEvalPersonas } from '../services/personaApi';
import { useTagsFromInitialData } from 'contexts/InitialDataContext';
import { ConversationArea } from '../components/ConversationArea';
import { CRITERIA_LABELS, getScoreColor } from '../components/JudgeEvaluationPanel';
import { useImprovementLoop, type EpisodeFields } from '../hooks/useImprovementLoop';
import { ImprovementConfigPanel, LoopProgress } from '../components/ImprovementConfigPanel';
import { ImprovementResultsView, generateReport } from '../components/ImprovementResultsView';
import { apiPut } from 'services/api';

export function EpisodeJudgeEvaluationView() {
  const { episodeId } = useParams<{ episodeId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const language = searchParams.get('language');

  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
  const [toolCalls, setToolCalls] = useState<ToolCallRecord[]>([]);
  const [evaluationResults, setEvaluationResults] = useState<EvaluateConversationResponse | null>(
    null
  );

  const evaluateConversationMutation = useEvaluateConversation();
  const runSimulationMutation = useRunSimulation();
  const contentJudgeMutation = useContentJudge();
  const summarizeJudgesMutation = useSummarizeJudges();
  const improveEpisodeMutation = useImproveEpisode();
  const { data: episodeData } = useFetchEpisodeForTesting(
    episodeId || undefined,
    language || undefined
  );
  const { data: evalPersonas } = useEvalPersonas();
  const tags = useTagsFromInitialData();

  // Multi-persona improvement loop
  const improvementLoop = useImprovementLoop(
    episodeData || null,
    evalPersonas,
    (params) => runSimulationMutation.mutateAsync(params),
    (params) => contentJudgeMutation.mutateAsync(params),
    (params) => summarizeJudgesMutation.mutateAsync(params),
    (params) => improveEpisodeMutation.mutateAsync(params)
  );

  // Load conversation history and tool calls from sessionStorage
  useEffect(() => {
    if (episodeId) {
      const conversationKey = `episode_conversation_${episodeId}`;
      const savedConversation = sessionStorage.getItem(conversationKey);
      if (savedConversation) {
        try {
          setConversationHistory(JSON.parse(savedConversation));
        } catch (error) {
          console.error('Error parsing conversation history:', error);
          toast.error('Failed to load conversation history');
        }
      }

      const toolCallsKey = `episode_tool_calls_${episodeId}`;
      const savedToolCalls = sessionStorage.getItem(toolCallsKey);
      if (savedToolCalls) {
        try {
          setToolCalls(JSON.parse(savedToolCalls));
        } catch (error) {
          console.error('Error parsing tool calls:', error);
        }
      }
    }
  }, [episodeId]);

  const handleEvaluate = async () => {
    if (!episodeId || !language || conversationHistory.length === 0) {
      toast.error('Missing required data for evaluation');
      return;
    }

    try {
      const results = await evaluateConversationMutation.mutateAsync({
        episodeId,
        language,
        conversationHistory,
        toolCalls,
      });

      setEvaluationResults(results);
      toast.success('Evaluation complete!');
    } catch (error) {
      toast.error('Failed to evaluate conversation');
      console.error('Error evaluating conversation:', error);
    }
  };

  const handleEditWithSuggestions = () => {
    if (!evaluationResults || !episodeId) return;

    const formattedRecommendations = evaluationResults.recommendations
      .map((rec, index) => `${index + 1}. ${rec}`)
      .join('\n');

    navigate(`/episodes/${episodeId}/edit`, {
      state: { recommendations: formattedRecommendations },
    });
  };

  const handleRunNewEvaluation = () => {
    setEvaluationResults(null);
    handleEvaluate();
  };

  const handleBackToTesting = () => {
    navigate(`/episodes/eval/${episodeId}`);
  };

  // Improvement loop handlers
  const handleStartImproveConfig = useCallback(() => {
    improvementLoop.openConfig();
  }, [improvementLoop]);

  const handleStartLoop = useCallback(() => {
    improvementLoop.startLoop();
  }, [improvementLoop]);

  const handleSaveToEpisode = useCallback(
    async (data: EpisodeFields) => {
      if (!episodeId) return;
      try {
        await apiPut(`/api/admin/episodes/${episodeId}`, {
          context: data.context,
          scenario: data.scenario,
          goals: data.goals,
          personas: data.personas,
        });
        toast.success('Episode updated successfully');
      } catch {
        toast.error('Failed to update episode');
      }
    },
    [episodeId]
  );

  const handleDownloadReport = useCallback(() => {
    if (!improvementLoop.state.baseline) return;
    const report = generateReport(
      improvementLoop.state.baseline,
      improvementLoop.state.iterations,
      improvementLoop.state.stopReason,
      improvementLoop.state.stopMessage
    );
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `improvement-report-${episodeId}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [improvementLoop.state, episodeId]);

  // Check if we have required data
  if (!episodeId || !language) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">Missing Information</h3>
          <p className="text-sm text-yellow-800 mb-4">
            Episode ID or language is missing. Please return to the testing page.
          </p>
          <Link
            to="/episodes/eval"
            className="text-sm font-medium text-yellow-700 hover:text-yellow-900"
          >
            &larr; Back to Episode Selector
          </Link>
        </div>
      </div>
    );
  }

  if (conversationHistory.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">No Conversation Found</h3>
          <p className="text-sm text-yellow-800 mb-4">
            No conversation history found for this episode. Please complete a test session first.
          </p>
          <button
            onClick={handleBackToTesting}
            className="text-sm font-medium text-yellow-700 hover:text-yellow-900"
          >
            &larr; Back to Testing
          </button>
        </div>
      </div>
    );
  }

  // Map tag IDs to tag names
  const episodeTags =
    tags && episodeData
      ? episodeData.tagIds
          .map((tagId) => tags.find((tag) => tag.id === tagId)?.name)
          .filter(Boolean)
      : [];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Judge Evaluation</h1>
        <button
          onClick={handleBackToTesting}
          className="text-blue-600 hover:text-blue-800 font-medium"
        >
          &larr; Back to Testing
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Context */}
        <div className="lg:col-span-1">
          {episodeData && (
            <div className="space-y-4 sticky top-4">
              <div className="bg-white border border-gray-300 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-3">Context</h2>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{episodeData.context}</p>
              </div>

              <div className="bg-white border border-gray-300 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Scenario</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{episodeData.scenario}</p>
              </div>

              <div className="bg-white border border-gray-300 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Episode Info</h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="font-medium text-gray-700">Name</p>
                    <p className="text-gray-900">{episodeData.name}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Complexity</p>
                    <p className="text-gray-900">{episodeData.complexityLevel}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Duration</p>
                    <p className="text-gray-900">
                      {episodeData.durationId === 1
                        ? 'Short'
                        : episodeData.durationId === 2
                          ? 'Medium'
                          : episodeData.durationId === 3
                            ? 'Long'
                            : 'Extended'}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Language</p>
                    <p className="text-gray-900">{episodeData.language.toUpperCase()}</p>
                  </div>
                  {episodeTags.length > 0 && (
                    <div>
                      <p className="font-medium text-gray-700 mb-1">Tags</p>
                      <div className="flex flex-wrap gap-1">
                        {episodeTags.map((tagName, index) => (
                          <span
                            key={index}
                            className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium"
                          >
                            {tagName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Middle Column: Conversation History */}
        <div className="lg:col-span-1">
          <div className="flex flex-col h-[700px]">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">Conversation History</h2>
              <p className="text-sm text-gray-600">
                Total exchanges: {Math.floor(conversationHistory.length / 2)}
              </p>
            </div>
            <ConversationArea messages={conversationHistory} />
          </div>
        </div>

        {/* Right Column: Goals, Personas & Evaluation */}
        <div className="lg:col-span-1">
          {episodeData && (
            <div className="space-y-4 sticky top-4">
              <div className="bg-white border border-gray-300 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Goals</h3>
                <ol className="space-y-1 text-xs text-gray-700 list-decimal list-inside">
                  {episodeData.goals.map((goal, index) => (
                    <li key={index}>{goal}</li>
                  ))}
                </ol>
              </div>

              <div className="bg-white border border-gray-300 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Personas</h3>
                <div className="space-y-2">
                  {episodeData.personas.map((persona, index) => (
                    <div key={index} className="bg-gray-50 rounded p-2">
                      <p className="font-semibold text-xs text-gray-900">{persona.name}</p>
                      <p className="text-xs text-gray-600">{persona.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Evaluation Section */}
              {!evaluationResults ? (
                <div className="bg-white border border-gray-300 rounded-lg p-4">
                  <h2 className="text-lg font-semibold text-gray-800 mb-3">Run Evaluation</h2>
                  <p className="text-sm text-gray-600 mb-4">
                    Evaluate this conversation using the Judge agent.
                  </p>
                  <button
                    onClick={handleEvaluate}
                    disabled={evaluateConversationMutation.isPending}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {evaluateConversationMutation.isPending ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Evaluating...
                      </span>
                    ) : (
                      'Evaluate with Judge'
                    )}
                  </button>
                </div>
              ) : (
                <EvaluationResultsCard
                  results={evaluationResults}
                  isPending={evaluateConversationMutation.isPending}
                  onReEvaluate={handleRunNewEvaluation}
                  onEditWithSuggestions={handleEditWithSuggestions}
                />
              )}

              {/* Improvement Loop Section */}
              {improvementLoop.state.status === 'idle' && (
                <div className="bg-white border border-gray-300 rounded-lg p-4">
                  <button
                    onClick={handleStartImproveConfig}
                    className="w-full px-4 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    Improve Episode (Multi-Persona)
                  </button>
                </div>
              )}

              {improvementLoop.state.status === 'configuring' && (
                <ImprovementConfigPanel
                  config={improvementLoop.state.config}
                  onConfigChange={improvementLoop.setConfig}
                  onStart={handleStartLoop}
                  onCancel={() => improvementLoop.discardAll()}
                />
              )}

              {['simulating', 'judging', 'summarizing', 'improving'].includes(
                improvementLoop.state.status
              ) && (
                <LoopProgress
                  currentIteration={improvementLoop.state.currentIteration}
                  maxIterations={improvementLoop.state.config.maxIterations}
                  status={improvementLoop.state.status}
                  simulatingPersonaIndex={improvementLoop.state.simulatingPersonaIndex}
                  totalPersonas={improvementLoop.state.config.selectedPersonaIds.length}
                  onCancel={improvementLoop.cancel}
                />
              )}

              {improvementLoop.state.status === 'complete' && improvementLoop.state.baseline && (
                <ImprovementResultsView
                  baseline={improvementLoop.state.baseline}
                  iterations={improvementLoop.state.iterations}
                  stopReason={improvementLoop.state.stopReason}
                  stopMessage={improvementLoop.state.stopMessage}
                  onSaveToEpisode={handleSaveToEpisode}
                  onDiscard={improvementLoop.discardAll}
                  onDownloadReport={handleDownloadReport}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Evaluation Results Card Component
// ============================================================================

interface EvaluationResultsCardProps {
  results: EvaluateConversationResponse;
  isPending: boolean;
  onReEvaluate: () => void;
  onEditWithSuggestions: () => void;
}

function EvaluationResultsCard({
  results,
  isPending,
  onReEvaluate,
  onEditWithSuggestions,
}: EvaluationResultsCardProps) {
  const [expandedCriteria, setExpandedCriteria] = useState<Set<string>>(new Set());

  const toggleCriteria = (key: string) => {
    setExpandedCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Results</h2>
        <button
          onClick={onReEvaluate}
          disabled={isPending}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          Re-evaluate
        </button>
      </div>

      {/* Overall Score */}
      <div className="mb-3 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-700">Overall Score</span>
          <span className="text-xl font-bold text-gray-900">
            {(results.overallScore * 100).toFixed(0)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${
              results.decision === 'PASS' ? 'bg-green-500' : 'bg-yellow-500'
            }`}
            style={{ width: `${results.overallScore * 100}%` }}
          />
        </div>
      </div>

      {/* Decision */}
      <div className="mb-3">
        <span
          className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
            results.decision === 'PASS'
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {results.decision}
        </span>
      </div>

      {/* Summary */}
      <div className="mb-3">
        <h3 className="text-xs font-semibold text-gray-700 mb-1">Summary</h3>
        <p className="text-xs text-gray-900">{results.summary}</p>
      </div>

      {/* Criteria Scores */}
      <div className="mb-3">
        <h3 className="text-xs font-semibold text-gray-700 mb-1">Criteria</h3>
        <div className="space-y-1">
          {Object.entries(results.criteriaScores).map(([key, cs]) => {
            const criteriaScore = cs as CriteriaScore;
            const label = CRITERIA_LABELS[key] || key.replace(/([A-Z])/g, ' $1').trim();
            const isExpanded = expandedCriteria.has(key);
            const hasEvidence = criteriaScore.evidence.length > 0;

            return (
              <div key={key}>
                <button
                  onClick={() => hasEvidence && toggleCriteria(key)}
                  className={`w-full flex items-center justify-between text-xs ${hasEvidence ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <span className="text-gray-600 flex items-center gap-1">
                    {label}
                    {hasEvidence && (
                      <span className="text-gray-400 text-[10px]">{isExpanded ? '▼' : '▶'}</span>
                    )}
                  </span>
                  <span
                    className="font-medium"
                    style={{ color: getScoreColor(criteriaScore.score) }}
                  >
                    {(criteriaScore.score * 100).toFixed(0)}%
                  </span>
                </button>
                {isExpanded && hasEvidence && (
                  <div className="mt-1 ml-2 pl-2 border-l-2 border-gray-200 mb-1">
                    {criteriaScore.evidence.map((ev, idx) => (
                      <p key={idx} className="text-[10px] text-gray-500 italic">
                        &ldquo;{ev}&rdquo;
                      </p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Violations */}
      {results.disallowedBehaviorViolations.length > 0 ? (
        <div className="mb-3">
          <h3 className="text-xs font-semibold text-red-800 mb-1">
            Violations ({results.disallowedBehaviorViolations.length})
          </h3>
          <div className="space-y-1">
            {results.disallowedBehaviorViolations.map((v, index) => (
              <div key={index} className="p-2 bg-red-50 border border-red-200 rounded text-xs">
                <p className="font-medium text-red-800">{v.rule}</p>
                <p className="text-red-700 italic">&ldquo;{v.evidence}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded flex items-center gap-1">
          <span className="text-green-600 text-xs">&#10003;</span>
          <span className="text-xs text-green-700">No violations detected</span>
        </div>
      )}

      {/* Strengths */}
      {results.strengths.length > 0 && (
        <div className="mb-3">
          <h3 className="text-xs font-semibold text-gray-700 mb-1">Strengths</h3>
          <ul className="space-y-1">
            {results.strengths.map((strength, index) => (
              <li key={index} className="text-xs text-gray-900 flex items-start">
                <span className="text-green-500 mr-1">&#10003;</span>
                {strength}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      {results.recommendations.length > 0 && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-xs font-semibold text-gray-700">Recommendations</h3>
            <button
              onClick={onEditWithSuggestions}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Edit Episode &rarr;
            </button>
          </div>
          <ul className="space-y-1">
            {results.recommendations.map((rec, index) => (
              <li key={index} className="text-xs text-gray-900 flex items-start">
                <span className="text-yellow-500 mr-1">&rarr;</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
